import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { normLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Lock } from "lucide-react";

const ADMIN_ROLES = ["admin", "super_admin"];

const TXT = {
  fr: {
    loginOk: "Connexion réussie", denied: "Accès refusé", loginFail: "Échec de la connexion",
    codeInvalid: "Code invalide", admin: "Administration", code2fa: "Code de vérification 2FA",
    code2faPh: "Code à 6 chiffres (ou code de secours)", verify: "Vérifier", cancel: "Annuler",
    email: "Email", password: "Mot de passe", signin: "Se connecter", footer: "Accès réservé au personnel autorisé.",
  },
  en: {
    loginOk: "Signed in successfully", denied: "Access denied", loginFail: "Sign-in failed",
    codeInvalid: "Invalid code", admin: "Administration", code2fa: "2FA verification code",
    code2faPh: "6-digit code (or backup code)", verify: "Verify", cancel: "Cancel",
    email: "Email", password: "Password", signin: "Sign in", footer: "Access restricted to authorized staff.",
  },
};

// Dedicated, unlinked admin console login (/control_SuperAdmin).
export default function AdminControlLogin() {
  const { i18n } = useTranslation();
  const L = TXT[normLang(i18n.language)] || TXT.fr;
  const { login, twofaLogin, logout } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [code, setCode] = useState("");
  const [denied, setDenied] = useState(false);

  const gate = async (u) => {
    if (u && ADMIN_ROLES.includes(u.role)) {
      toast.success(L.loginOk);
      nav("/admin");
    } else {
      await logout();
      setTempToken(null);
      setDenied(true);
      toast.error(L.denied);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setDenied(false);
    try {
      const res = await login(email, password);
      if (res && res.requires_2fa) setTempToken(res.temp_token);
      else await gate(res);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || L.loginFail);
    } finally {
      setLoading(false);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await gate(await twofaLogin(tempToken, code));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || L.codeInvalid);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4" data-testid="admin-control-login">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-800 bg-slate-900">
            <ShieldCheck className="h-7 w-7 text-accent" />
          </div>
          <h1 className="font-display text-xl font-bold text-white">{L.admin}</h1>
          <p className="mt-1 text-sm text-slate-400">The World Promotions</p>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 shadow-2xl">
          {denied && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-900/60 bg-red-950/40 px-3 py-2 text-sm text-red-300" data-testid="admin-access-denied">
              <Lock className="h-4 w-4" /> {L.denied}
            </div>
          )}

          {tempToken ? (
            <form onSubmit={submit2fa} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-300">{L.code2fa}</label>
                <input required value={code} onChange={(e) => setCode(e.target.value)} data-testid="admin-2fa-code"
                  placeholder={L.code2faPh}
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-accent" />
              </div>
              <button disabled={loading} data-testid="admin-2fa-submit"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-accent py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} {L.verify}
              </button>
              <button type="button" onClick={() => { setTempToken(null); setCode(""); }} className="w-full text-sm text-slate-500 hover:text-slate-300">{L.cancel}</button>
            </form>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-300">{L.email}</label>
                <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email"
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-accent" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-300">{L.password}</label>
                <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password"
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-accent" />
              </div>
              <button disabled={loading} data-testid="admin-login-submit"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-accent py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} {L.signin}
              </button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-slate-600">{L.footer}</p>
      </div>
    </div>
  );
}
